import { supabase } from '@saas-boilerplate/shared';
import { startTimer } from './perf';

/** Minimal headers for Edge Functions (GET etc.): always send anon `apikey`; Bearer is user JWT or anon. */
export function supabaseEdgeInvokeHeaders(accessToken?: string | null): Record<string, string> {
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  const bearer = accessToken || apikey;
  const h: Record<string, string> = {};
  if (apikey) h.apikey = apikey;
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

/** Headers for browser `fetch()` to Edge Functions - gateway expects `apikey` + user JWT for CORS POST from SPA. */
export function supabaseEdgeFetchHeaders(accessToken: string): Record<string, string> {
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  if (apikey) h.apikey = apikey;
  return h;
}

export async function parseFunctionsInvokeError(error: unknown): Promise<string> {
  const e = error as { message?: string; context?: Response | { body?: string | object } };
  const ctx = e.context;
  if (ctx instanceof Response) {
    try {
      const text = await ctx.text();
      try {
        const b = JSON.parse(text) as { error?: string; hint?: string };
        if (typeof b.error === 'string') return b.error;
        if (typeof b.hint === 'string') return b.hint;
      } catch {
        if (text) return text.slice(0, 300);
      }
    } catch {
      /* ignore */
    }
  } else if (ctx && typeof ctx === 'object' && 'body' in ctx) {
    const raw = (ctx as { body?: string | object }).body;
    if (raw != null) {
      try {
        const b = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (b && typeof b === 'object') {
          if (typeof (b as { error?: string }).error === 'string') return (b as { error: string }).error;
          if (typeof (b as { hint?: string }).hint === 'string') return (b as { hint: string }).hint;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return e.message || 'Request failed';
}

/**
 * Invoke Edge Functions with a real user JWT (not anon fallback).
 */
export async function invokeEdgeFn<T = unknown>(name: string, body: Record<string, unknown>): Promise<T> {
  // Perf instrumentation: one timed span per edge call, app-wide. Include the provider for calendar
  // calls so per-provider latency stands out (e.g. `edge:calendar-list-events(microsoft)`).
  const detail = typeof body?.provider === 'string' ? `(${body.provider})` : '';
  const stop = startTimer(`edge:${name}${detail}`);
  try {
    let {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: ref } = await supabase.auth.refreshSession();
      session = ref.session ?? null;
    }
    if (!session?.access_token) {
      throw new Error('Your session expired. Please sign in again.');
    }

    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
    const { data, error } = await supabase.functions.invoke(name, {
      body,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        ...(anon ? { apikey: anon } : {}),
      },
    });
    if (error) {
      if (import.meta.env.DEV) {
        console.warn(`[edge ${name}]`, error.message, (error as { context?: unknown }).context);
      }
      throw new Error(await parseFunctionsInvokeError(error));
    }
    return data as T;
  } finally {
    stop();
  }
}
